import { Location } from 'react-router';
import {
  CustomMusic,
  Hymn,
  HymnalSlug,
  SongFileExtension,
  SongListEntry,
  SongManifestEntry,
  SongNumber,
  SongSlug,
} from '../../../common/src/model.ts';
import { isBound, isUnbound } from '../../../common/src/model_util.ts';
import { Pages } from '../../../common/src/pages.ts';
import {
  encodeMusicURI,
  encodeSongSlug,
  getSongSlug,
  hymnalsPDFDir,
  musicManifestFilename,
} from '../../../common/src/paths.ts';
import { ensureExists } from '../../../common/src/util.ts';

import { useLocation } from 'react-router-dom';
import { HymnalWithHymns } from '../data/hymnals.ts';

export type UrlPrefix = string;

export function generateFileName({
  songNumber,
  title,
  psalm,
}: {
  songNumber: SongNumber;
  title: string;
  psalm?: string;
}) {
  const titleAllCaps = ensureExists(title).toUpperCase();
  // Titles that are themselves a psalm (English "Psalm N" or French "Psaume N") must match the
  // server's manifest parser (server/src/music/song.ts nameParts), which sets psalm === title.
  if (titleAllCaps.match(/^(?:PSALM|PSAUME) [1-9]\d*$/)) {
    return songNumber + ' - ' + title;
  } else {
    if (psalm) {
      return songNumber + ' - ' + title + ' - ' + psalm;
    } else {
      return songNumber + ' - ' + title;
    }
  }
}

export function generatePathFor(base: string, hymnalName: string, fileName: string) {
  // Cantus Christi hymnals were renamed to include parenthesis (e.g. "Cantus Christi (2002)")
  // Some orgs still have the old links (e.g. The King's Congregation)
  if (hymnalName.includes('Cantus Christi') && !hymnalName.includes('(')) {
    hymnalName = hymnalName.replace(/(\d+)/, '($1)');
  }
  return `${base}/${encodeMusicURI(hymnalName)}/${encodeMusicURI(fileName)}`;
}

export function getPageFromLocation(location: Pick<Location, 'pathname'> | undefined): Pages | undefined {
  if (!location) {
    return;
  }
  const pagePart = location.pathname.match(/\/(?<page>[^/]+)/)?.groups?.page;
  return pagePart && Object.values(Pages).includes(pagePart as Pages) ? (pagePart as Pages) : undefined;
}

// the Organization tab's hymnal sub-page (/organization/hymnal)
export const orgHymnalSubPage = 'hymnal';

export function getUrlForPage(page: Pages) {
  return '/' + page;
}

export function getSongSlugParts(songSlug: SongSlug):
  | {
      hymnalOrChurch: string;
      hymnalSlugOrChurchId: string;
      songNumber: string;
    }
  | undefined {
  // see SongSlug declaration for slug format examples
  const parts = songSlug.split('/');
  if (parts.length !== 4) {
    return undefined;
  }
  const [hymnalOrChurch, hymnalSlugOrChurchId, songKeyword, songNumber] = parts;
  if (songKeyword !== 'song') {
    console.log(`malformed song slug: ${songSlug}`);
    return undefined;
  }
  return { hymnalOrChurch, hymnalSlugOrChurchId, songNumber };
}

export function useCurrentPage() {
  const location = useLocation();
  return getPageFromLocation(location);
}

export function getHymnFromSlug({
  songSlug,
  hymnals,
  customMusic,
}: {
  songSlug?: SongSlug;
  hymnals?: HymnalWithHymns[];
  customMusic?: CustomMusic;
}): Hymn | undefined {
  if (!songSlug) {
    return;
  }
  const parts = getSongSlugParts(songSlug);
  if (!parts) {
    return;
  }
  const { hymnalOrChurch, hymnalSlugOrChurchId } = parts;
  if (hymnalOrChurch === 'church') {
    if (!customMusic) {
      return;
    }
    const hymns = customMusic[hymnalSlugOrChurchId] ?? [];
    return hymns.find((hymn) => hymn.slug === songSlug);
  } else {
    if (!hymnals || hymnals.length === 0) {
      return;
    }
    const hymnal = hymnals.find((hymnal) => hymnal.slug.toLowerCase() === hymnalSlugOrChurchId.toLowerCase());
    if (!hymnal) {
      console.log(`No hymnal for slug ${hymnalSlugOrChurchId.toLowerCase()}, hymnal count: ${hymnals.length}`, hymnals);
      return;
    }
    return hymnal.hymns.find((hymn) => hymn.slug === songSlug);
  }
}

export function getSongSlugForHymn(
  hymnals: HymnalWithHymns[],
  hymn: { songNumber: SongNumber; hymnal: string; basePath?: string },
): SongSlug | undefined {
  const { songNumber } = hymn;
  let hymnalSlug: HymnalSlug | undefined;
  let churchId: string | undefined;
  if (hymn.basePath) {
    churchId = hymn.hymnal.match(/^(?<churchId>\d+)/)?.groups?.churchId;
    if (!churchId) {
      console.error(`Cannot parse org Id from hymnal "${hymn.hymnal}" for hymn with basePath ${hymn.basePath}`);
      return;
    }
  } else {
    hymnalSlug = hymnals.find((hymnal) => hymnal.name === hymn.hymnal)?.slug;
    if (!hymnalSlug) {
      console.error(`Hymn not found in hymnals #${songNumber}, hymnal "${hymn.hymnal}", basePath ${hymn.basePath}`);
      return;
    }
  }
  return getSongSlug({ songNumber, hymnalSlug, orgId: churchId ? Number(churchId) : undefined });
}

// NOTE: logic must match bubbleExactNumericMatchesToTop
export function resolveSearchStringToSongSlug(
  hymnals: HymnalWithHymns[] | undefined,
  customHymns: Hymn[] | undefined,
  text: string,
  isInternalUser: boolean,
): SongSlug | undefined {
  if (!text) {
    return;
  }
  const filteredHymnals =
    hymnals?.filter(({ isInternal, isChurchSpecific }) => (!isInternal && !isChurchSpecific) || isInternalUser) ?? [];

  // prioritize exact display name matches for copy/paste within planner
  for (const hymnal of filteredHymnals ?? []) {
    for (const hymn of hymnal.hymns) {
      if (getDisplayNameForHymn(hymn) === text) {
        return hymn.slug;
      }
    }
  }

  const maybeSongNumber = !!text.match(/^\d/);
  // prioritize exact song number matches
  if (maybeSongNumber) {
    for (const hymn of customHymns ?? []) {
      // for custom songs, ignore hymn.number, but see if the title starts with the song number
      if (hymn.title.startsWith(text) && hymn.title[text.length] === ' ') {
        return hymn.slug;
      }
    }
    // prioritize exact hymn number matches above title matches (ignoring custom song numbers)
    // include hymns with letter suffix - for example, '135' matches '135a'
    for (const hymnal of filteredHymnals ?? []) {
      for (const hymn of hymnal.hymns) {
        const { songNumber } = hymn;
        if (
          !isCustomSong(hymn.slug) &&
          (songNumber === text ||
            (songNumber.startsWith(text) &&
              text.length + 1 === songNumber.length &&
              songNumber[text.length].match(/[a-z]/)))
        ) {
          return hymn.slug;
        }
      }
    }
  }
  for (const hymn of customHymns ?? []) {
    if (hymn.title.includes(text)) {
      return hymn.slug;
    }
  }
  for (const hymnal of filteredHymnals ?? []) {
    for (const hymn of hymnal.hymns) {
      if (hymn.title.includes(text)) {
        return hymn.slug;
      }
    }
  }
}

// NOTE: logic must match resolveSearchStringToSongSlug
export function bubbleExactNumericMatchesToTop(hymns: Hymn[], searchString: string) {
  const maybeSongNumber = !!searchString.match(/^\d/);
  if (!maybeSongNumber) {
    return hymns;
  }
  let targetIndex = 0;
  for (let index = 0; index < hymns.length; index++) {
    if (isCustomSong(hymns[index].slug)) {
      // treat custom song name as if it might have a numeric prefix, and prioritize exact match
      if (hymns[index].title.startsWith(searchString) && hymns[index].title[searchString.length] === ' ') {
        hymns.splice(targetIndex, 0, ...hymns.splice(index, 1));
        targetIndex++;
      }
      // ignore actual hymn.number of custom songs
      continue;
    }
    // include hymns with letter suffix - for example, '135' matches '135a'
    const songNumber = hymns[index].songNumber;
    if (
      songNumber === searchString ||
      (songNumber.startsWith(searchString) &&
        searchString.length + 1 === songNumber.length &&
        songNumber[searchString.length].match(/[a-z]/))
    ) {
      hymns.splice(targetIndex, 0, ...hymns.splice(index, 1));
      targetIndex++;
    }
  }
  return hymns;
}

export function isCustomSong(songSlug: SongSlug) {
  return songSlug.startsWith('church');
}

export function getHymnFromSongListEntry({
  song,
  hymnals,
  customMusic,
}: {
  song: SongListEntry;
  hymnals?: HymnalWithHymns[];
  customMusic: CustomMusic;
}): Hymn | undefined {
  if (!isBound(song)) {
    return undefined;
  }
  return getHymnFromSlug({ songSlug: song.slug, hymnals, customMusic });
}

export function generateHymnUrlFromSlug({ songSlug, page }: { songSlug: SongSlug; page?: Pages }): string | undefined {
  return `${getUrlForPage(page ?? Pages.Library)}?songSlug=${encodeSongSlug(songSlug)}`;
}

export function generateHymnUrl({
  hymnals,
  hymn,
  page,
}: {
  hymnals: HymnalWithHymns[];
  hymn: Hymn;
  page?: Pages;
}): string | undefined {
  const songSlug = getSongSlugForHymn(hymnals, hymn);
  if (!songSlug) {
    return;
  }
  return generateHymnUrlFromSlug({ songSlug, page });
}

function getUrlForHymn(hymn: Hymn, fileExtension: string) {
  const filename = generateFileName(hymn) + fileExtension;
  const basePath = hymn.basePath ?? `/${hymnalsPDFDir}`;
  return generatePathFor(basePath, hymn.hymnal, filename);
}

export function getPdfUrlForHymn(hymn: Hymn) {
  return getUrlForHymn(hymn, '.' + SongFileExtension.Pdf);
}

export function getSypUrlForHymn(hymn: Hymn) {
  return getUrlForHymn(hymn, '.' + SongFileExtension.Syp);
}

export function getMidiUrlForHymn(hymn: Hymn): string {
  return getUrlForHymn(hymn, '.' + SongFileExtension.Mid);
}

// The single helper for building a song's url prefix (issue #3517). Favorites, the org hymnal
// pre-download and the manifest walk must agree exactly, including encoding, or cache listeners
// and the cached-prefix matching in use_downloads silently miss songs whose hymnal name or song
// number contains characters that need encoding (e.g. '!', '#', '&', "'", '(', ')').
export function getUrlPrefixForSong({
  hymnalName,
  songNumber,
  basePath,
}: {
  hymnalName: string;
  songNumber: SongNumber;
  basePath?: string;
}): UrlPrefix {
  return generatePathFor(basePath ?? `/${hymnalsPDFDir}`, hymnalName, songNumber);
}

export function getUrlPrefixForHymn(hymn: Hymn): UrlPrefix {
  return getUrlPrefixForSong({ hymnalName: hymn.hymnal, songNumber: hymn.songNumber, basePath: hymn.basePath });
}

// A child of a music manifest: the base url is already encoded, so only the key is encoded here.
export function getChildUrlPrefix(baseUrl: UrlPrefix, key: string, isManifest?: boolean): UrlPrefix {
  const escapedKey = encodeMusicURI(key);
  if (isManifest) {
    return `${baseUrl}/${escapedKey}/${musicManifestFilename}`;
  }
  return `${baseUrl}/${escapedKey}`;
}

export function getUrlForSongManifestEntry({
  basePath,
  songNumber,
  manifestEntry,
  extension,
}: {
  basePath: string;
  songNumber: SongNumber;
  manifestEntry: SongManifestEntry;
  extension: SongFileExtension;
}): string | undefined {
  const { title, psalm } = manifestEntry;
  const filename = encodeMusicURI(generateFileName({ songNumber, title, psalm }));
  return `${basePath}/${filename}.${extension}`;
}

export function getDisplayNameForSongListEntry({
  entry,
  hymnals,
  customMusic,
}: {
  entry?: SongListEntry;
  hymnals?: HymnalWithHymns[];
  customMusic?: CustomMusic;
}): string | undefined {
  if (isBound(entry)) {
    const hymn = getHymnFromSlug({ songSlug: entry.slug, hymnals, customMusic });
    return hymn && (isCustomSong(entry.slug) ? hymn.title : getDisplayNameForHymn(hymn));
  } else if (isUnbound(entry)) {
    return entry.text;
  }
}

export function getDisplayNameForHymn(hymn: Hymn) {
  return `${hymn.songNumber} - ${hymn.title}`;
}

export const PrimaryOrgHymnalId = Number.MAX_SAFE_INTEGER;
